import type { TravelQuery } from "@goblin/domain";

const BASE = import.meta.env.VITE_API_BASE_URL || "";

export type SearchEvent =
  | { type: "stage_started"; stage: string }
  | {
      type: "stage_completed";
      stage: string;
      candidatesIn?: number;
      candidatesOut?: number;
      elapsedMs: number;
    }
  | { type: "offer"; offer: unknown }
  | { type: "budget_warning"; message: string; scope: "monthly" | "daily" | "session" }
  | { type: "done"; totalOffers: number; elapsedMs: number }
  | { type: "error"; stage?: string; message: string };

export type SearchOpts = {
  /** Override the flight provider for this request. Default: Worker's GOBLIN_FLIGHT_PROVIDER. */
  flightProvider?: string;
};

/**
 * Submit a TravelQuery and yield SearchEvents as they stream in via SSE.
 */
export async function* streamSearch(
  query: TravelQuery,
  opts: SearchOpts = {},
): AsyncGenerator<SearchEvent> {
  const url = new URL("/v1/search", BASE || window.location.origin);
  if (opts.flightProvider) url.searchParams.set("flight_provider", opts.flightProvider);
  const resp = await fetch(url.toString(), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(query),
  });
  if (!resp.ok || !resp.body) {
    const text = await resp.text().catch(() => "");
    throw new Error(`search failed: ${resp.status} ${text}`);
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() ?? "";
    for (const block of events) {
      const event = parseSseBlock(block);
      if (event) yield event;
    }
  }
}

function parseSseBlock(block: string): SearchEvent | null {
  const lines = block.split("\n");
  let data = "";
  for (const line of lines) {
    if (line.startsWith("data: ")) data += line.slice(6);
  }
  if (!data) return null;
  try {
    return JSON.parse(data) as SearchEvent;
  } catch {
    return null;
  }
}

export async function health(): Promise<{ ok: boolean }> {
  const r = await fetch(`${BASE}/healthz`, { credentials: "include" });
  return r.json();
}
